import React from 'react';

type Props = {
  /** Changes when the page's template or its settings change: the canvas tries again. */
  resetKey: string;
  children: React.ReactNode;
};
type State = { error: string | null };

/**
 * Keeps the editor usable when the page's template can't be drawn (for example a
 * template setting it can't read): the outline and Page settings still work, so
 * the setting can be fixed, and the canvas tries again once it changes.
 */
export default class CanvasBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error: error && error.message ? error.message : String(error) };
  }

  componentDidCatch(error: Error) {
    console.error('The page canvas failed to draw:', error); // eslint-disable-line no-console
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null }); // eslint-disable-line react/no-did-update-set-state
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" data-testid="canvas-error" style={{ margin: 32, padding: '16px 18px', borderRadius: 8, border: '1px solid #E8B4B4', background: '#FBE9E9', color: '#8A1C1C', fontSize: 14, lineHeight: 1.5 }}>
        <strong>The page couldn’t be drawn here.</strong>
        <div style={{ marginTop: 4 }}>{this.state.error}</div>
        <div style={{ marginTop: 8 }}>Check the template’s settings in Page settings. The outline and the other panels still work.</div>
      </div>
    );
  }
}
